import { Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Divider, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useGetFacilityBreakConfiguration } from "@src/appV2/Shifts/Shift/useGetFacilityBreakConfiguration";
import { type Worker } from "@src/appV2/Worker/api/types";
import { type UseQueryResult } from "@tanstack/react-query";
import { useRouteMatch } from "react-router-dom";

import { type ShiftBlockDetail } from "../../redesign/ShiftDiscovery/Blocks/api/useGetShiftBlock";
import {
  getHourlyPayForShiftBlock,
  SHIFT_BLOCK_ACTIONS,
  transformIntoShiftBlockWithDetails,
} from "../../redesign/ShiftDiscovery/Blocks/utils";
import { BookShiftBlockButton } from "./BookShiftBlockButton";
import { FacilityChatButtonContainer } from "./FacilityChatButtonContainer";
import { PriorityAccessAlert } from "./PriorityAccessAlert";
import { ShiftBlockFacilityDetails } from "./ShiftBlockFacilityDetails";
import { ShiftBlockHeader } from "./ShiftBlockHeader";
import { ShiftBlockShiftDetails } from "./ShiftBlockShiftDetails";

interface ShiftBlockDetailProps {
  worker: Worker;
  shiftBlock: ShiftBlockDetail;
  isShiftBlockNegotiable: boolean;
  refetchShiftBlock: UseQueryResult<ShiftBlockDetail>["refetch"];
}

export function ShiftBlockDetails(props: ShiftBlockDetailProps) {
  const { worker, shiftBlock, refetchShiftBlock, isShiftBlockNegotiable } = props;
  const { path: currentRouterUrl } = useRouteMatch();

  const { mandatoryBreakDurationInMinutes } = useGetFacilityBreakConfiguration({
    workplaceId: shiftBlock.data.attributes.facilityId,
  });

  const shiftBlockWithMappedDetails = transformIntoShiftBlockWithDetails(
    shiftBlock.data,
    shiftBlock.included
  );
  const {
    id: shiftBlockId,
    facility,
    shifts,
    isShiftBlockAssigned,
    priorityAccess,
    orientationBlockId: orientationBlockIdProperty,
  } = shiftBlockWithMappedDetails;

  useLogEffect(APP_V2_USER_EVENTS.SHIFT_BLOCKS, {
    action: SHIFT_BLOCK_ACTIONS.BLOCK_DETAILS_VIEWED,
    shiftBlockId,
    facilityId: facility.id,
    isShiftBlockAssigned,
    location: currentRouterUrl,
    priorityAccess,
  });

  const hasPriorityAccess = isDefined(priorityAccess) && isDefined(priorityAccess.priorityTill);
  const orientationBlockId = isDefined(orientationBlockIdProperty)
    ? orientationBlockIdProperty
    : undefined;

  return (
    <Stack spacing={2}>
      <ShiftBlockHeader
        shiftBlockWithDetails={shiftBlockWithMappedDetails}
        agentTimezone={worker.tmz}
        mandatoryBreakDurationInMinutes={mandatoryBreakDurationInMinutes}
      />

      {hasPriorityAccess && (
        <Stack spacing={1}>
          <PriorityAccessAlert priorityAccessTill={priorityAccess.priorityTill ?? ""} />
        </Stack>
      )}
      <ShiftBlockFacilityDetails facility={facility} isShiftBlockAssigned={isShiftBlockAssigned} />
      {isDefined(shiftBlock.data.attributes.notesForProfessionals) && (
        <>
          <Stack spacing={0.5}>
            <Text
              sx={{
                fontWeight: 600,
              }}
            >
              Block Information
            </Text>
            <Text
              variant="body2"
              sx={{
                color: (theme) => theme.palette.text.primary,
                opacity: 0.8,
              }}
            >
              {shiftBlock.data.attributes.notesForProfessionals}
            </Text>
          </Stack>

          <Divider />
        </>
      )}
      {isDefined(worker) && (
        <FacilityChatButtonContainer
          shiftBlockId={shiftBlockId}
          agentId={worker.userId}
          facilityId={facility.id}
          isShiftBlockAssigned={isShiftBlockAssigned}
        />
      )}

      <ShiftBlockShiftDetails
        shiftBlockWithDetails={shiftBlockWithMappedDetails}
        agentTimezone={worker.tmz}
        mandatoryBreakDurationInMinutes={mandatoryBreakDurationInMinutes}
      />

      {!isShiftBlockAssigned && (
        <Box sx={{ position: "absolute", bottom: "24px", left: "24px", right: "24px" }}>
          <BookShiftBlockButton
            isBlockNegotiable={isShiftBlockNegotiable}
            facilityId={shiftBlock.data.attributes.facilityId}
            agentId={worker.userId}
            shiftBlockId={shiftBlock.data.id}
            shifts={shifts}
            blockAgentRequirement={shiftBlock.data.attributes.agentReq}
            instantBookPreferredReasons={
              shiftBlock.data.attributes.instantBook?.preferredReasons ?? []
            }
            hourlyPayForShiftBlock={getHourlyPayForShiftBlock(shiftBlockWithMappedDetails)}
            workplaceName={facility.name}
            workerName={worker.name}
            orientationBlockId={orientationBlockId}
            onBookingShiftBlock={refetchShiftBlock}
          />
        </Box>
      )}
    </Stack>
  );
}
